import { Check, ChevronDown, Clock, Filter, Loader2, Pause, Play } from 'lucide-react'
import { useState } from 'react'
import {
  type AudioGroup,
  type AudioTrackBindingItem,
  type AudioTrackBindingKind,
  type AudioTrackBindingState
} from '../../../api'
import type { Label } from '../../../api/labels'
import { EmptyState, IconButton, MobileEntityList, MobileEntityRow, SearchField } from '../../../components/ui'
import { labelCategoryLabels } from '../../labels/labelDisplay'
import { labelChipClassName, labelMoreClassName } from '../../labels/labelUiStyles'
import { formatBpm } from '../audioFormatting'
import { formatDuration } from '../../visuals/formatDuration'
import { AudioAssignmentsModeSheet, type AudioAssignmentsModeOption } from '../AudioAssignmentsModeSheet'
import { AudioAssignmentsTargetPickerDialog } from '../AudioAssignmentsTargetPickerDialog'
import styles from './AudioAssignmentsMobileList.module.css'

type AudioAssignmentsMobileListProps = {
  activeLabels: Label[]
  assignedTotal: number
  groups: AudioGroup[]
  items: AudioTrackBindingItem[]
  kind: AudioTrackBindingKind
  loading: boolean
  loadingTrackId: string | null
  onPlayTrack: (track: AudioTrackBindingItem) => void
  onSearchInputChange: (value: string) => void
  onStateChange: (state: AudioTrackBindingState) => void
  onTargetSelect: (kind: AudioTrackBindingKind, targetId: string) => void
  onToggleBinding: (track: AudioTrackBindingItem) => void
  pendingTrackIds: string[]
  playingTrackId: string | null
  searchInput: string
  state: AudioTrackBindingState
  targetId: string
  unassignedTotal: number
  ungroupedTotal: number
}

const cx = (...classes: Array<string | false | null | undefined>): string => classes.filter(Boolean).join(' ')

const mobileStateLabels: Record<AudioTrackBindingState, string> = {
  all: 'All',
  assigned: 'Assigned',
  unassigned: 'Missing',
  ungrouped: 'No groups'
}

const mobileStateDescriptions: Record<AudioTrackBindingState, string> = {
  all: 'All tracks for the selected target',
  assigned: 'Tracks already assigned to it',
  unassigned: 'Tracks missing this assignment',
  ungrouped: 'Tracks without any group'
}

const mobileStateBadges: Record<AudioTrackBindingState, string> = {
  all: 'All',
  assigned: 'On',
  unassigned: 'Miss',
  ungrouped: 'None'
}

export const AudioAssignmentsMobileList = ({
  activeLabels,
  assignedTotal,
  groups,
  items,
  kind,
  loading,
  loadingTrackId,
  onPlayTrack,
  onSearchInputChange,
  onStateChange,
  onTargetSelect,
  onToggleBinding,
  pendingTrackIds,
  playingTrackId,
  searchInput,
  state,
  targetId,
  unassignedTotal,
  ungroupedTotal
}: AudioAssignmentsMobileListProps) => {
  const [targetPickerOpen, setTargetPickerOpen] = useState(false)
  const [modeSheetOpen, setModeSheetOpen] = useState(false)
  const selectedLabel = kind === 'label' ? activeLabels.find((label) => label.id === targetId) ?? null : null
  const selectedGroup = kind === 'group' ? groups.find((group) => group.id === targetId) ?? null : null
  const targetName = selectedLabel?.name ?? selectedGroup?.name ?? (kind === 'label' ? 'Select tag' : 'Select group')
  const targetTypeLabel = selectedLabel
    ? labelCategoryLabels[selectedLabel.category]
    : kind === 'label' ? 'Tag' : 'Group'
  const stateOptions: AudioAssignmentsModeOption[] = [
    { count: assignedTotal + unassignedTotal, description: mobileStateDescriptions.all, label: mobileStateLabels.all, value: 'all' },
    { count: assignedTotal, description: mobileStateDescriptions.assigned, label: mobileStateLabels.assigned, value: 'assigned' },
    { count: unassignedTotal, description: mobileStateDescriptions.unassigned, label: mobileStateLabels.unassigned, value: 'unassigned' },
    ...(kind === 'group'
      ? [{ count: ungroupedTotal, description: mobileStateDescriptions.ungrouped, label: mobileStateLabels.ungrouped, value: 'ungrouped' as const }]
      : [])
  ]
  const activeStateOption = stateOptions.find((option) => option.value === state) ?? {
    count: assignedTotal + unassignedTotal,
    description: mobileStateDescriptions.all,
    label: mobileStateLabels.all,
    value: 'all' as const
  }

  return (
    <MobileEntityList
      className={styles.root ?? ''}
      empty={items.length === 0 ? <EmptyState message={loading ? 'Loading assignments...' : 'No tracks match these filters.'} /> : null}
      toolbar={(
        <div className={styles.toolbar}>
          <button
            aria-label={`Change assignment target. Current target: ${targetName}`}
            className={styles.targetButton}
            onClick={() => setTargetPickerOpen(true)}
            type="button"
          >
            <span className={styles.targetCopy}>
              <span>{kind === 'label' ? targetTypeLabel : 'Group'}</span>
              <strong>{targetName}</strong>
            </span>
            <ChevronDown size={17} />
          </button>

          <div className={styles.searchRow}>
            <SearchField
              className={styles.searchField}
              onChange={(event) => onSearchInputChange(event.target.value)}
              placeholder="Search tracks..."
              size="mobile"
              value={searchInput}
            />
            <button
              aria-label={`Change assignment mode. Current mode: ${activeStateOption.label}`}
              className={styles.modeButton}
              onClick={() => setModeSheetOpen(true)}
              title={`Mode: ${activeStateOption.label}`}
              type="button"
            >
              <Filter size={17} />
              <span>{mobileStateBadges[state]}</span>
            </button>
          </div>

          {targetPickerOpen && (
            <AudioAssignmentsTargetPickerDialog
              activeLabels={activeLabels}
              groups={groups}
              kind={kind}
              onClose={() => setTargetPickerOpen(false)}
              onSelect={onTargetSelect}
              targetId={targetId}
            />
          )}

          {modeSheetOpen && (
            <AudioAssignmentsModeSheet
              onClose={() => setModeSheetOpen(false)}
              onSelect={onStateChange}
              options={stateOptions}
              value={state}
            />
          )}
        </div>
      )}
    >
      {items.map((track) => {
        const pending = pendingTrackIds.includes(track.id)
        const isLoadingTrack = loadingTrackId === track.id
        const isPlayingTrack = playingTrackId === track.id
        const visibleLabelCount = 3
        const hiddenLabelCount = Math.max(0, track.labels.length - visibleLabelCount)
        const groupNames = track.groups.map((group) => group.name).join(', ')

        return (
          <MobileEntityRow
            actions={(
              <div className={styles.playWrap}>
                {track.status === 'ready' ? (
                  <IconButton
                    disabled={isLoadingTrack}
                    icon={isLoadingTrack ? <Loader2 size={15} /> : isPlayingTrack ? <Pause size={15} /> : <Play size={15} />}
                    iconSize="play"
                    label={isPlayingTrack ? `Pause ${track.title}` : `Play ${track.title}`}
                    loading={isLoadingTrack}
                    onClick={() => onPlayTrack(track)}
                    title={isPlayingTrack ? 'Pause' : 'Play'}
                  />
                ) : (
                  <span className={`track-status-icon track-status-icon-${track.status}`} title={track.status}>
                    <Clock size={15} />
                  </span>
                )}
              </div>
            )}
            className={styles.row ?? ''}
            key={track.id}
            leading={(
              <button
                aria-label={`${track.assigned ? 'Remove' : 'Add'} selected assignment for ${track.title}`}
                className={cx(styles.toggle, track.assigned ? styles.toggleActive : '')}
                disabled={pending || !targetId}
                onClick={() => onToggleBinding(track)}
                type="button"
              >
                {track.assigned && <Check size={17} />}
              </button>
            )}
            subtitle={(
              <span className={styles.subtitle}>
                <span className={styles.meta}>{track.artist || 'Unknown artist'} · {formatBpm(track.effective_bpm)} · {formatDuration(track.duration_seconds) ?? '-'}</span>
                {track.labels.length > 0 ? (
                  <span className={styles.labelRow}>
                    {track.labels.slice(0, visibleLabelCount).map((label) => (
                      <span className={labelChipClassName(label.category)} key={`${label.category}-${label.id}`}>
                        {label.name}
                      </span>
                    ))}
                    {hiddenLabelCount > 0 && <span className={labelMoreClassName}>+{hiddenLabelCount}</span>}
                  </span>
                ) : (
                  <span className="muted">No tags</span>
                )}
                <span className={styles.groups}>{groupNames || 'No groups'}</span>
              </span>
            )}
            title={track.title}
          />
        )
      })}
    </MobileEntityList>
  )
}
